import { CboRecordDto } from '@act/shared/data-transfer-objects';
import { Button, FlexContainer, ResourceCard } from '@act/storybook';
import type { IconName } from '@act/storybook';
import { PlacesSearch } from 'react-components/places/PlacesSearch';
import type { CboResourceFormValues } from './cboResourceForm.schema';
import { mapResourceDetailsToResourceCardProps } from './mapToResourceDetails.util';
import type { ResourceCardNeedIconOptions } from './resourceCardIconForNeed.util';

export interface ResourceBrowsePanelProps {
  recentRecords: CboRecordDto[];
  placesSearchEnabled?: boolean;
  resourceCardIconName?: IconName;
  needIconOptions?: ResourceCardNeedIconOptions;
  onRecentSelect: (record: CboRecordDto) => void;
  onPlacesAutofill: (partial: Partial<CboResourceFormValues>) => void;
  /** When omitted, manual entry is triggered from a parent footer (e.g. call fullscreen). */
  onAddCustomResource?: () => void;
  onRemove?: () => void;
  removeDisabled?: boolean;
}

/**
 * Browse step for resource entry: recent CBOs, Places search, and custom manual entry.
 *
 * @param props - Handlers and optional recent cache rows.
 * @returns Browse UI shared by calls and need actions.
 */
export function ResourceBrowsePanel({
  recentRecords,
  placesSearchEnabled = true,
  resourceCardIconName,
  needIconOptions,
  onRecentSelect,
  onPlacesAutofill,
  onAddCustomResource,
  onRemove,
  removeDisabled = false,
}: ResourceBrowsePanelProps) {
  return (
    <FlexContainer direction="column" gap={2} width="100%" overflowAllVisible>
      {recentRecords.length > 0 ? (
        <FlexContainer direction="column" gap={1} width="100%" overflowAllVisible>
          {recentRecords.map(record => (
            <ResourceCard
              key={record.id}
              {...mapResourceDetailsToResourceCardProps(
                {
                  resourceName: record.name,
                  categoryLabel: record.categoryLabel ?? undefined,
                  contactName: record.contactName ?? undefined,
                  phoneNumber: record.phoneNumber ?? undefined,
                  url: record.websiteUrl ?? undefined,
                  streetAddress: record.streetAddress ?? undefined,
                  city: record.city ?? undefined,
                  state: record.state ?? undefined,
                  zipcode: record.zipcode ?? undefined,
                  formattedAddress: record.formattedAddress ?? undefined,
                },
                needIconOptions
              )}
              iconName={resourceCardIconName}
              onClick={() => onRecentSelect(record)}
            />
          ))}
        </FlexContainer>
      ) : null}

      {placesSearchEnabled ? (
        <PlacesSearch
          showLabel={false}
          showSelectionSummary={false}
          placeholder="Search for resource"
          onFormAutofill={onPlacesAutofill}
        />
      ) : null}

      {onAddCustomResource ? (
        <Button variant="outlined" onClick={onAddCustomResource}>
          Enter resource manually
        </Button>
      ) : null}

      {onRemove ? (
        <Button variant="text" onClick={onRemove} disabled={removeDisabled}>
          Remove
        </Button>
      ) : null}
    </FlexContainer>
  );
}
